<script setup lang="ts">
import type { BreadcrumbItem, ResponsiveHeaderAction, ResponsiveHeaderMode } from '@hprofits/ui';
import {
  ArrowLeftIcon,
  Badge,
  Breadcrumbs,
  Button,
  CloseIcon,
  CogIcon,
  PageShellHeader,
  PageShellHeaderLeft,
  PageShellHeaderRight,
  ResponsiveHeaderActions,
  ResponsiveHeaderItem,
  SaveIcon,
  useConfirm,
} from '@hprofits/ui';
import { runDetached } from '@hprofits/utils';
import { computed, onMounted, onUnmounted } from 'vue';
import { RouterLink } from 'vue-router';

import { t } from '@/i18n';
import { HeaderProfileMenu, HeaderThemeToggle } from '@/modules/shared/components';

import { useBlacklistEditorContext } from '../../composables/context';

defineOptions({ name: 'BlacklistAutoRuleEditorHeader' });

type HeaderResponsiveSlot = 'breadcrumbs' | 'minimalBreadcrumbs' | 'status';
type HeaderResponsiveConfig = Readonly<Record<HeaderResponsiveSlot, ReadonlyArray<ResponsiveHeaderMode>>>;

const HEADER_RESPONSIVE_CONFIG: HeaderResponsiveConfig = {
  breadcrumbs: ['wide', 'compact'],
  minimalBreadcrumbs: ['minimal'],
  status: ['wide'],
};

const LIST_ROUTE = { name: 'blacklist-auto-rules-poc' } as const;

const { title, parentGroup, loading, ready, canSubmit, isPending, removePending, discard, save } =
  useBlacklistEditorContext();
const confirm = useConfirm();

// Save and Clear stay enabled even with no changes (product decision): they only lock while the
// editor is loading or a save or delete is in flight — a save racing a delete would write to a rule
// being removed. `canSubmit` drives the unsaved badge and Cmd+S.
const isInteractive = computed(() => ready.value && !isPending.value && !removePending.value);

const breadcrumbs = computed<ReadonlyArray<BreadcrumbItem>>(() => [
  { label: t('modules.blacklistAutoRules.title'), to: LIST_ROUTE },
  ...(parentGroup.value
    ? [
        {
          label: parentGroup.value.name,
          to: { name: 'blacklist-rule-group-details', params: { groupId: parentGroup.value.id } },
        },
      ]
    : []),
  { label: title.value },
]);
const minimalBreadcrumbs = computed(() => breadcrumbs.value.slice(-1));
// The same destination as the breadcrumb above the title and as the return after a save.
const backRoute = computed(() =>
  parentGroup.value ? { name: 'blacklist-rule-group-details', params: { groupId: parentGroup.value.id } } : LIST_ROUTE,
);

const handleSaveShortcut = (event: KeyboardEvent): void => {
  if (!(event.ctrlKey || event.metaKey) || event.key !== 's') {
    return;
  }
  // Swallowed even when there is nothing to save, or the browser opens its own "Save page" dialog.
  event.preventDefault();
  if (canSubmit.value) {
    runDetached(save.actions.submit());
  }
};

const handleDiscard = async (): Promise<void> => {
  if (!isInteractive.value) {
    return;
  }
  const confirmed = await confirm({
    title: t('modules.blacklistAutoRules.editor.discardTitle'),
    description: t('modules.blacklistAutoRules.editor.discardDescription'),
    confirmText: t('modules.blacklistAutoRules.editor.discardConfirm'),
  });
  if (confirmed) {
    discard();
  }
};

const headerActions = computed<ReadonlyArray<ResponsiveHeaderAction>>(() => [
  {
    id: 'discard',
    label: t('modules.blacklistAutoRules.editor.discard'),
    // A labelled button when wide; on narrower widths just the cross (label stays as aria text).
    icon: CloseIcon,
    presentation: {
      wide: 'button',
      compact: 'icon',
      minimal: 'icon',
    },
    variant: 'outline',
    disabled: !isInteractive.value,
    onClick: handleDiscard,
  },
  {
    id: 'save',
    label: t('modules.blacklistAutoRules.editor.save'),
    icon: SaveIcon,
    // Save must stay visible at every width: a button when there is room, an icon on minimal.
    presentation: {
      wide: 'button',
      compact: 'button',
      minimal: 'icon',
    },
    variant: 'filled',
    disabled: !isInteractive.value,
    loading: isPending.value,
    onClick: async () => {
      await save.actions.submit();
    },
  },
]);

onMounted(() => {
  window.addEventListener('keydown', handleSaveShortcut);
});
onUnmounted(() => {
  window.removeEventListener('keydown', handleSaveShortcut);
});
</script>

<template>
  <PageShellHeader>
    <PageShellHeaderLeft>
      <Button
        :as="RouterLink"
        :to="backRoute"
        variant="blank"
        size="3"
        :icon="ArrowLeftIcon"
        :aria-label="$t('modules.blacklistAutoRules.editor.back')"
        square
      />
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.breadcrumbs">
        <Breadcrumbs :items="breadcrumbs" :loading="loading" item-max-width="280px" />
      </ResponsiveHeaderItem>
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.minimalBreadcrumbs">
        <Breadcrumbs :items="minimalBreadcrumbs" :loading="loading" item-max-width="280px" />
      </ResponsiveHeaderItem>
    </PageShellHeaderLeft>
    <PageShellHeaderRight>
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.status">
        <Transition name="fade">
          <Badge v-if="canSubmit" size="4" variant="soft" color="yellow" :icon="SaveIcon">
            {{ $t('modules.blacklistAutoRules.editor.unsaved') }}
          </Badge>
        </Transition>
      </ResponsiveHeaderItem>
      <ResponsiveHeaderActions :actions="headerActions" :menu-icon="CogIcon" menu-variant="outline" />
      <HeaderThemeToggle />
      <HeaderProfileMenu />
    </PageShellHeaderRight>
  </PageShellHeader>
</template>
